import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Une fenêtre au-dessus de l'écran. Échap ou un clic sur le fond la ferment ;
 * le premier champ prend le focus, et le focus revient où il était à la fermeture.
 */
export function Modale({
  titre,
  description,
  onFermer,
  children,
  pied,
  large = false,
}: {
  titre: string;
  description?: string | undefined;
  onFermer: () => void;
  children: ReactNode;
  pied?: ReactNode;
  large?: boolean;
}) {
  const cadre = useRef<HTMLDivElement>(null);
  const fermer = useRef(onFermer);
  const idTitre = useId();

  useEffect(() => {
    fermer.current = onFermer;
  }, [onFermer]);

  /* Une seule fois à l'ouverture : relancé à chaque rendu, le focus sauterait
     au premier champ pendant qu'on tape dans le deuxième. */
  useEffect(() => {
    const precedent = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const premier = cadre.current?.querySelector<HTMLElement>(
      'input:not([type=hidden]), select, textarea, button[data-autofocus]'
    );
    (premier ?? cadre.current)?.focus();
    function echap(evenement: KeyboardEvent) {
      if (evenement.key === 'Escape') fermer.current();
    }
    window.addEventListener('keydown', echap);
    return () => {
      window.removeEventListener('keydown', echap);
      precedent?.focus();
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-nav/40 px-4 py-[8vh]"
      onMouseDown={onFermer}
    >
      <div
        ref={cadre}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitre}
        className={`w-full ${large ? 'max-w-2xl' : 'max-w-lg'} rounded-2xl border border-border bg-surface shadow-xl outline-none`}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="flex items-start gap-3 border-b border-border-subtle px-6 py-4">
          <div className="flex-1">
            <h2 id={idTitre} className="text-lg font-semibold">
              {titre}
            </h2>
            {description ? <p className="mt-0.5 text-[13px] text-muted">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer"
            className="grid size-8 place-items-center rounded-lg text-muted hover:bg-surface-hover hover:text-ink"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
        {pied ? (
          <div className="flex justify-end gap-2 border-t border-border-subtle px-6 py-4">
            {pied}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
